// Filter Registry - Central export for all filter configurations
// This is the single source of truth for both frontend and backend

// Re-export types
export * from './types';

// Re-export factory functions
export * from './factories';

// Re-export report filter configurations
export { REPORT_FILTER_CONFIGS } from './reporting';

// Import entity filter registries
import { accessLogsFilters } from './access-logs';
import { automationFilters } from './automation';
import { callHistoryFilters } from './call-history';
import { clientFilters } from './client';
import { conversationFilters } from './conversation';
import { distributionFilters } from './distribution';
import { ecommerceFilters } from './ecommerce';
import { emailFilters } from './email';
import { invoiceFilters } from './invoice';
import { issueFilters } from './issue';
import { itemFilters } from './item';
import { leadEligibilityFilters } from './lead-eligibility';
import { licenseFilters } from './license';
import { licenseAuditFilters } from './license-audit';
import { licenseDisciplinaryFilters } from './license-disciplinary';
import { licenseDiscrepanciesFilters } from './license-discrepancies';
import { licenseLinksFilters } from './license-links';
import { licenseMatchFilters } from './license-match';
import { opportunityFilters } from './opportunity';
import { orderFilters } from './order';
import { paymentFilters } from './payment';
import { promoFilters } from './promo';
import { purchaseOrderFilters } from './purchase-order';
import { purchaseOrderReceiptFilters } from './purchase-order-receipt';
import { quoteFilters } from './quote';
import { registryFilters } from './registry';
import { routeFilters } from './route';
import { sharedFilters } from './shared';
import { sourcingRequestFilters } from './sourcing-request';
import { statementFilters } from './statement';
import { taskFilters } from './task';
import { teamFilters } from './team';
import { vendorFilters } from './vendor';
import { warehouseFilters } from './warehouse';

import type { FilterRegistry } from './types';

/**
 * Merge multiple filter registries with duplicate key detection
 * Throws an error if any duplicate keys are found
 */
function mergeWithDuplicateCheck(
	...registries: Array<FilterRegistry>
): FilterRegistry {
	const result: FilterRegistry = {};

	for (const registry of registries) {
		for (const key of Object.keys(registry)) {
			if (result[key] != null) {
				throw new Error(
					`Duplicate filter key detected: "${key}". Each filter must have a unique key across all registries.`,
				);
			}

			const filterConfig = registry[key];

			if (filterConfig != null) {
				result[key] = filterConfig;
			}
		}
	}

	return result;
}

/**
 * Shared Filter Registry
 * Used by both frontend and backend to ensure consistency
 * This is the SINGLE SOURCE OF TRUTH for all filter definitions
 */
export const SHARED_FILTER_REGISTRY: FilterRegistry = mergeWithDuplicateCheck(
	sharedFilters,
	clientFilters,
	taskFilters,
	teamFilters,
	leadEligibilityFilters,
	orderFilters,
	invoiceFilters,
	quoteFilters,
	opportunityFilters,
	issueFilters,
	itemFilters,
	licenseFilters,
	licenseMatchFilters,
	licenseAuditFilters,
	licenseDisciplinaryFilters,
	licenseDiscrepanciesFilters,
	licenseLinksFilters,
	sourcingRequestFilters,
	callHistoryFilters,
	automationFilters,
	accessLogsFilters,
	emailFilters,
	conversationFilters,
	vendorFilters,
	warehouseFilters,
	paymentFilters,
	ecommerceFilters,
	purchaseOrderFilters,
	purchaseOrderReceiptFilters,
	routeFilters,
	promoFilters,
	registryFilters,
	distributionFilters,
	statementFilters,
);
